import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { FaGithub, FaLinkedinIn } from "react-icons/fa";
import { FiMail } from "react-icons/fi";
import { Bio } from "../../data/constants";
import { fadeUp, stampIn, staggerContainer, viewport } from "../../utils/motion";
import StarMark from "../common/StarMark";

// Dark green footer biome closing the page: star cluster, square social chips,
// sign-off. Same twinkling mark as the hero, scaled down.

const FooterContainer = styled.footer`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 56px 22px 48px;
  background: ${({ theme }) => theme.biome.footer.bg};
  border-top: ${({ theme }) => theme.frameWidth} solid
    ${({ theme }) => theme.frameInk};

  @media (max-width: 768px) {
    border-top-width: ${({ theme }) => theme.frameWidthSm};
  }
`;

const Inner = styled(motion.div)`
  width: 100%;
  max-width: ${({ theme }) => theme.maxWidth};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 22px;
`;

const Socials = styled(motion.div)`
  display: flex;
  gap: 8px;
`;

const SocialIcon = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 0;
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: ${({ theme }) => theme.biome.footer.ink};
  font-size: 15px;
  transition: background 120ms linear, color 120ms linear;

  &:hover {
    background: ${({ theme }) => theme.orange};
    border-color: ${({ theme }) => theme.orange};
    color: ${({ theme }) => theme.frameInk};
  }
`;

const Signoff = styled(motion.span)`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 13px;
  color: ${({ theme }) => theme.biome.footer.ink};
`;

const Copyright = styled(motion.span)`
  font-family: ${({ theme }) => theme.fonts.ui};
  font-size: 11px;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.biome.footer.muted};
`;

// Twinkling stars close the page — the one thing that keeps the final frame
// alive without a soft glow.
const Stars = () => (
  <motion.div variants={fadeUp}>
    <StarMark size={46} />
  </motion.div>
);

const socials = [
  { href: Bio.linkedin, label: "LinkedIn", Icon: FaLinkedinIn },
  { href: `mailto:${Bio.email}`, label: "Email", Icon: FiMail },
  { href: Bio.github, label: "GitHub", Icon: FaGithub },
];

const Footer = () => (
  <FooterContainer>
    <Inner
      variants={staggerContainer(0.08)}
      initial="hidden"
      whileInView="show"
      viewport={viewport}
    >
      <Stars />

      <Socials variants={staggerContainer(0.06)}>
        {socials.map(({ href, label, Icon }) => {
          const external = !href.startsWith("mailto:");
          return (
            <SocialIcon
              key={label}
              variants={stampIn}
              href={href}
              aria-label={label}
              target={external ? "_blank" : undefined}
              rel={external ? "noreferrer" : undefined}
            >
              <Icon />
            </SocialIcon>
          );
        })}
      </Socials>

      <Signoff variants={fadeUp}>the adventure never ends.</Signoff>
      <Copyright variants={fadeUp}>
        &copy; {new Date().getFullYear()} {Bio.name}
      </Copyright>
    </Inner>
  </FooterContainer>
);

export default Footer;
